import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, useBCMSContext, useStateManager } from "@bcm-dev/bcms-vite";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from 'components/shared';
import { CheckmarkBanner } from 'components/shared/views/ConfirmBannerView';
import { PrimaryButton } from 'components/ui';
import Classes, { UnlinkedAccount } from "model";
import React from 'react';
import { SVGAsset } from 'res';
import MobileManageRecipientAccountsTable from './components/MobileManageRecipientAccountsTable';
import './mobile.css';
import './styles.css';

/**
 * @function ManageRecipientAccountsPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows manage recipient accounts
 */

export default function ManageRecipientAccountsPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  
  const data = { ...getDefaultData(context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const className = props.className ? (" " + props.className) : "";

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onSuccessfullyAddedUnlinkedAccountBannerClose: () => {
          BCMSStateManagerCRUD.init(state)
          .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], false)
          .submit(setState);
      },
  });

  return(
    <PageContainer className={"manage-recipient-accounts-page" + className} state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = (controller, _accounts) => {
  const accounts = _accounts ?? UnlinkedAccount.initAll().sort((a, b) => b.id - a.id);

  return {
    accounts,
    addedUnlinkedAccount: undefined,
  };
}

export const getDefaultUI = () => ({
  successfullyAddedUnlinkedAccount: { isActive: false },
});

function PageContent(props) {
  const { data, ui, onSuccessfullyAddedUnlinkedAccountBannerClose } = useStateManager();
  const accounts = data.accounts ?? [];

  return (
    <PageContentView>
      <div className="flex-container flex-vertical page-widget-wrapper">
        { ui.successfullyAddedUnlinkedAccount.isActive
        && <CheckmarkBanner text={ `"${ data.addedUnlinkedAccount.nickname }" has been added as an unlinked account. ` }
         dataPoint="" onClick={ onSuccessfullyAddedUnlinkedAccountBannerClose } /> }

        <CenterContentContainer>
          <div className="upper-content">
            <ContentHeaderView text="Manage Recipient Accounts" />
            <div className="show-on-desktop">
              <DisabledPrompt>
                <Link className="add-recipient-account-link" dataPoint="Add_Account_Button">
                  + Add <span className="manage-recipient-fi-abbr">CPB</span> Recipient Account
                </Link>
              </DisabledPrompt>
            </div>
            <div className="show-on-mobile">
              <DisabledPrompt>
                <PrimaryButton
                  className="add-recipient-account-button"
                  dataPoint="Add_Account_Button"
                  text={ <>ADD <span className="manage-recipient-fi-abbr">CPB</span> RECIPIENT ACCOUNT</> }
                />
              </DisabledPrompt>
            </div>
          </div>
          <div className="show-on-desktop">
            <ManageRecipientAccountsTable accounts={ accounts } />
          </div>
          <div className="show-on-mobile">
            <MobileManageRecipientAccountsTable accounts={ accounts } />
          </div>
        </CenterContentContainer>
      </div>
    </PageContentView>
  );
}

/**
 * @function ManageRecipientAccountsTable
 * @return {React.Component}
 * @exported false
 * @description The manage recipient accounts table
 */

function ManageRecipientAccountsTable(props) {
  const accounts = props.accounts ?? [];
  const rows = accounts.reduce((acc, account) => {
    acc.push(
      <ManageRecipientAccountsTableRow key={ account.id } account={ account } />
    );
    return acc;
  }, []);

  return(
    <TableView className="manage-recipient-accounts-table">
      <TableColumnContainer>
        <TableColumnView text="Account Number" />
        <TableColumnView text="Account Nickname" icon="ArrowDownIcon" />
        <TableColumnView text="Date Added" />
        <TableColumnView text="" />
        <TableColumnView text="" />
        <TableColumnView text="" />
      </TableColumnContainer>
      <TableBody>
        { rows }
      </TableBody>
    </TableView>
  );
}

/**
 * @function ManageRecipientAccountsTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row in the manage recipient accounts table
 */

function ManageRecipientAccountsTableRow(props) {
  const account = props.account ?? {};
  const dateAdded = `${ account.dateAdded.format("m/d/Y") } ${ account.dateAdded.format("h:i p") }`;

  return(
    <TableItemContainer className="manage-recipient-accounts-row">
      <TableItemTextView text={ account.number } />
      <TableItemTextView text={ account.nickname } />
      <TableItemTextView text={ dateAdded } />
      <TableItemView>
        <DisabledPrompt>
          <Link dataPoint={ `Unlinked_Transfer_Accounts_View_Transactions_Link_${ account.id }` }>
            View Transactions
          </Link>
        </DisabledPrompt>
      </TableItemView>
      <TableItemView>
        <DisabledPrompt>
          <SVGAsset
            id="PencilIcon"
            className="clickable table-icon"
            data-point={ `Unlinked_Transfer_Accounts_Edit_Account_Link_${ account.id }` }
          />
        </DisabledPrompt>
      </TableItemView>
      <TableItemView>
        <DisabledPrompt>
          <SVGAsset
            id="TrashcanXIcon"
            className="clickable table-icon"
            data-point={ `Unlinked_Transfer_Accounts_Remove_Account_Link_${ account.id }` }
          />
        </DisabledPrompt>
      </TableItemView>
    </TableItemContainer>
  );
}
